/**
 * ReportViewer — renders selected report file.
 *
 * Fetches file via /channels/portal/workspace/file (JSON with content_type + content).
 * Renders based on content_type:
 *   - text/html -> iframe via blob URL
 *   - text/markdown -> MarkdownRenderer
 *   - text/csv -> table (capped rows; Download for the full file)
 *   - other -> raw text
 */

import { useLocale } from '../../i18n/useLocale';
import { formatNumber } from '../../i18n/format';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useRef } from 'react';
import { gatewayClient, WorkspaceFile, WorkspaceFileResponse } from '../../client/GatewayClient';
import { useReportMetadata, parseReportMetadata } from '../../context/ReportMetadataContext';
import { useWorkspaceNav } from '../../context/WorkspaceNavContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';
import MarkdownRenderer from '../ChatPanel/MarkdownRenderer';
import { parseCsv } from './parseCsv';
import WorkspaceHome from '../WorkspaceHome/WorkspaceHome';
import './ReportViewer.css';

interface ReportViewerProps {
  file: WorkspaceFile | null;
  /** Called when a file is confirmed to no longer exist */
  onFileNotFound?: (key: string) => void;
}

const NON_RENDERABLE_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx',
  'zip', 'rar', '7z', 'tar', 'gz',
  'exe', 'dmg', 'msi',
  'mp3', 'mp4', 'avi', 'mov', 'wmv', 'flv',
  'psd', 'ai', 'sketch',
]);

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);

function getFileExtension(file: WorkspaceFile | null): string {
  if (!file) return '';
  return (file.name || file.key).split('.').pop()?.toLowerCase() || '';
}

function isNonRenderableByExtension(file: WorkspaceFile | null): boolean {
  if (!file) return false;
  return NON_RENDERABLE_EXTENSIONS.has(getFileExtension(file));
}

function isImageByExtension(file: WorkspaceFile | null): boolean {
  if (!file) return false;
  return IMAGE_EXTENSIONS.has(getFileExtension(file));
}

// An audience export can carry tens of thousands of rows. Rendering all of them is
// one <td> per cell, so 50k x 6 is 300k DOM nodes and the tab stops responding —
// the viewer is for eyeballing the shape of the data, and Download covers the rest.
const CSV_MAX_RENDERED_ROWS = 500;

function CsvTable({ text }: { text: string }) {
  const { t } = useTranslation('workspace');
  const { locale } = useLocale();
  const rows = useMemo(() => parseCsv(text), [text]);
  // Nothing parseable (empty file): fall back to raw rather than an empty table,
  // so a truncated or malformed export is visible instead of looking like no data.
  if (rows.length === 0) return <pre className="report-viewer__raw">{text}</pre>;

  const [header, ...body] = rows;
  const shown = body.slice(0, CSV_MAX_RENDERED_ROWS);
  const hidden = body.length - shown.length;

  return (
    <div className="report-viewer__csv">
      <div className="report-viewer__csv-note">
        {hidden > 0
          ? t('viewer.csvTruncated', { shown: formatNumber(shown.length, locale), total: formatNumber(body.length, locale) })
          : t('viewer.csvTotal', { total: formatNumber(body.length, locale) })}
      </div>
      <div className="report-viewer__csv-scroll">
        <table className="report-viewer__csv-table">
          <thead>
            <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((r, ri) => (
              // Cells are indexed off the header, not off the row, so a short or
              // over-long row cannot shift the columns of the whole table.
              <tr key={ri}>{header.map((_, ci) => <td key={ci}>{r[ci] ?? ''}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ReportViewer({ file, onFileNotFound }: ReportViewerProps) {
  const { t } = useTranslation('workspace');
  const [fileData, setFileData] = useState<WorkspaceFileResponse | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // A server message is shown as-is; a fallback is a key, translated at render so it
  // follows the locale without re-running the load effects.
  const [error, setError] = useState<{ message: string } | { key: 'loadImageFailed' | 'loadReportFailed' } | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const imageBlobUrlRef = useRef<string | null>(null);
  const { setMetadata } = useReportMetadata();
  const { refreshWorkspace, setSelectedFile } = useWorkspaceNav();
  const { effectiveUid, isImpersonating } = useImpersonation();
  const { currentAgent } = useAgent();
  const userParam = isImpersonating ? effectiveUid : undefined;
  /** Track whether we've already retried for this file key */
  const retriedRef = useRef<string | null>(null);

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (imageBlobUrlRef.current) {
      URL.revokeObjectURL(imageBlobUrlRef.current);
      imageBlobUrlRef.current = null;
    }
    if (!file) { setFileData(null); setBlobUrl(null); setImageBlobUrl(null); setMetadata(null); return; }

    // For binary/non-renderable files, verify existence but don't parse content
    if (isNonRenderableByExtension(file)) {
      let cancelled = false;
      setLoading(true); setError(null); setDownloadError(false); setFileData(null); setBlobUrl(null); setImageBlobUrl(null); setMetadata(null);
      (async () => {
        try {
          await gatewayClient.getWorkspaceFile(currentAgent, `output/${file.key}`, userParam);
          if (!cancelled) setLoading(false);
        } catch (err) {
          if (cancelled) return;
          const errMsg = err instanceof Error ? err.message : '';
          const isNotFound = errMsg.toLowerCase().includes('not found') || errMsg.includes('404');
          if (isNotFound) {
            refreshWorkspace();
            onFileNotFound?.(file.key);
            setSelectedFile(null);
          }
          setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    // For image files, download as blob and render via <img>
    if (isImageByExtension(file)) {
      let cancelled = false;
      setLoading(true); setError(null); setFileData(null); setBlobUrl(null); setImageBlobUrl(null); setMetadata(null);
      (async () => {
        try {
          const blob = await gatewayClient.downloadWorkspaceFile(currentAgent, `output/${file.key}`, userParam);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          imageBlobUrlRef.current = url;
          setImageBlobUrl(url);
        } catch (err) {
          if (cancelled) return;
          const errMsg = err instanceof Error ? err.message : '';
          const isNotFound = errMsg.toLowerCase().includes('not found') || errMsg.includes('404');
          if (isNotFound) {
            refreshWorkspace();
            onFileNotFound?.(file.key);
            setSelectedFile(null);
          } else {
            setError(errMsg ? { message: errMsg } : { key: 'loadImageFailed' });
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    let cancelled = false;
    async function loadFile() {
      setLoading(true); setError(null); setFileData(null); setBlobUrl(null); setImageBlobUrl(null); setMetadata(null);
      retriedRef.current = null; // Reset retry tracker for each new load
      try {
        const resp = await gatewayClient.getWorkspaceFile(currentAgent, `output/${file!.key}`, userParam);
        if (cancelled) return;
        setFileData(resp);
        if (resp.content_type === 'text/html') {
          const blob = new Blob([resp.content], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);

          // Parse report-metadata from HTML content
          const meta = parseReportMetadata(resp.content);
          setMetadata(meta);
        } else {
          setMetadata(null);
        }
      } catch (err) {
        if (cancelled) return;
        const errMsg = err instanceof Error ? err.message : '';
        const isNotFound = errMsg.toLowerCase().includes('not found') || errMsg.includes('404');

        if (isNotFound && retriedRef.current !== file!.key) {
          // First time not-found for this file — refresh workspace and retry
          retriedRef.current = file!.key;
          refreshWorkspace();
          // Retry after a short delay to allow workspace list to refresh
          setTimeout(async () => {
            if (cancelled) return;
            try {
              const resp = await gatewayClient.getWorkspaceFile(currentAgent, `output/${file!.key}`, userParam);
              if (cancelled) return;
              setFileData(resp);
              setError(null);
              if (resp.content_type === 'text/html') {
                const blob = new Blob([resp.content], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setBlobUrl(url);
                const meta = parseReportMetadata(resp.content);
                setMetadata(meta);
              } else {
                setMetadata(null);
              }
            } catch (retryErr) {
              if (cancelled) return;
              const retryMsg = retryErr instanceof Error ? retryErr.message : '';
              const retryIsNotFound = retryMsg.toLowerCase().includes('not found') || retryMsg.includes('404');
              if (retryIsNotFound) {
                onFileNotFound?.(file!.key);
                setSelectedFile(null);
              } else {
                setError(retryMsg ? { message: retryMsg } : { key: 'loadReportFailed' });
              }
              setMetadata(null);
            } finally {
              if (!cancelled) setLoading(false);
            }
          }, 1500);
          return; // keep loading state while retrying
        } else if (isNotFound) {
          // Already retried — file truly gone
          onFileNotFound?.(file!.key);
          setSelectedFile(null);
          setMetadata(null);
          setLoading(false);
          return;
        }

        setError(errMsg ? { message: errMsg } : { key: 'loadReportFailed' });
        setMetadata(null);
      } finally {
        // Only set loading=false if we're not in the retry path
        if (!cancelled && retriedRef.current !== file!.key) setLoading(false);
      }
    }
    loadFile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.key, userParam, currentAgent]);

  useEffect(() => () => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    if (imageBlobUrlRef.current) URL.revokeObjectURL(imageBlobUrlRef.current);
  }, []);

  const displayName = useMemo(() => {
    if (!file) return '';
    const name = file.name || file.key.split('/').pop() || '';
    return name.replace(/\.[^.]+$/, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.key]);

  const handleDownload = async () => {
    const fileName = file?.name || file?.key.split('/').pop() || 'file';
    if (isImageByExtension(file) && imageBlobUrl) {
      const a = document.createElement('a');
      a.href = imageBlobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    if (!fileData) return;
    const blob = new Blob([fileData.content], { type: fileData.content_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBinaryDownload = async () => {
    if (!file || downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const blob = await gatewayClient.downloadWorkspaceFile(currentAgent, `output/${file.key}`, userParam);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || file.key.split('/').pop() || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  if (!file) {
    return (
      <div className="report-viewer">
        <WorkspaceHome />
      </div>
    );
  }

  function renderContent() {
    if (loading) return <div className="report-viewer__loading">{t('viewer.loading')}</div>;
    if (error) return <div className="report-viewer__error">⚠️ {'message' in error ? error.message : t(`viewer.${error.key}`)}</div>;

    if (isNonRenderableByExtension(file)) {
      const fileName = file?.name || file?.key.split('/').pop() || 'file';
      return (
        <div className="report-viewer__non-renderable">
          <div className="report-viewer__non-renderable-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4 2.9 4 4v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="#e8eaed" stroke="#5f6b7a" strokeWidth="1.2"/>
              <path d="M14 2v6h6" fill="#fff" stroke="#5f6b7a" strokeWidth="1.2" strokeLinejoin="round"/>
              <path d="M9 13h6M9 17h4" stroke="#5f6b7a" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </div>
          <p className="report-viewer__non-renderable-text">
            {t('viewer.cannotPreview')}
          </p>
          <p className="report-viewer__non-renderable-filename">{fileName}</p>
          <button className="report-viewer__non-renderable-download" onClick={handleBinaryDownload} disabled={downloading}>
            {downloading ? t('viewer.downloading') : t('viewer.download')}
          </button>
          {downloadError && <p className="report-viewer__non-renderable-error">{t('viewer.downloadFailed')}</p>}
        </div>
      );
    }

    if (isImageByExtension(file) && imageBlobUrl) {
      return (
        <div className="report-viewer__image-container">
          <img className="report-viewer__image" src={imageBlobUrl} alt={displayName} />
        </div>
      );
    }

    if (!fileData) return null;

    if (fileData.content_type === 'text/html' && blobUrl) {
      // SECURITY: do NOT add `allow-same-origin`. The rendered HTML comes from a
      // blob: URL, which inherits THIS app's origin. Combining `allow-same-origin`
      // with `allow-scripts` lets agent-generated report HTML execute JS with full
      // parent-origin access — including reading the non-HttpOnly
      // rocky_federate_id_token cookie (token theft -> account takeover). Omitting
      // it runs the frame in an opaque origin: scripts still work but cannot read
      // the parent's cookies or DOM.
      return <iframe className="report-viewer__iframe" src={blobUrl} title={displayName} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" />;
    }

    if (fileData.content_type === 'text/markdown' || fileData.content_type === 'text/x-markdown') {
      return <div className="report-viewer__markdown"><MarkdownRenderer markdown={fileData.content} /></div>;
    }

    if (fileData.content_type === 'text/csv') {
      return <CsvTable text={fileData.content} />;
    }

    return <pre className="report-viewer__raw">{fileData.content}</pre>;
  }

  return (
    <div className="report-viewer">
      <div className="report-viewer__topbar">
        <div className="report-viewer__title-area">
          <h1 className="report-viewer__title">{displayName}</h1>
        </div>
        <div className="report-viewer__actions">
          <button
            className={`report-viewer__download-btn${(fileData || imageBlobUrl) ? ' report-viewer__download-btn--active' : ''}`}
            onClick={handleDownload}
            disabled={!fileData && !imageBlobUrl}
            title={t('viewer.downloadTitle')}
          >
            <span className="report-viewer__download-icon">↓</span>
            <span>{t('viewer.download')}</span>
          </button>
        </div>
      </div>
      <div className="report-viewer__body">{renderContent()}</div>
    </div>
  );
}
